// Program.js
import React, { useEffect, useMemo, useState } from "react";
import NavigationBar from "./components/NavigationBar";
import Footer from "./components/Footer";
import "./globals.css";

/**
 * LEARNING OBJECTIVES
 * - Key by a unique session id you choose (used in each keynote row)
 * - Each entry must be an array of 3 items (you can edit later)
 */
const LEARNING_OBJECTIVES = {
  "thu-s1-0800": [
    "1.	Review advances in CT and function MR imaging of the airways.",
    "2.	Discuss their clinical utility over current assessment of severe asthma.",
    "3.	Illustrate, with clinical examples, assessment of unexplained breathlessness, airflow obstruction, and choice of therapies.",
  ],
  "thu-s1-0815": [
    "1.	Review new imaging phenotypes and relationship to underlying disease process in asthma.",
    "2.	Identify treatable targets in airways disease using imaging.",
    "3.	Describe response to biologic and other therapies using imaging.",
  ],
  "thu-0840":[
    "1. Identify and explain why measures such as spirometry and ventilation defect percent (VDP) may fail to capture significant regional ventilation changes following bronchodilator use.",
    "2. Describe the methodology of Response Mapping (RM) and identify the added benefits of this technique over VDP.",
    "3. Recognize the phenotype of patients who appear to be bronchodilator 'non-responders' by conventional methods but exhibit high regional ventilation redistribution."
  ],
  "thu-0850":[
    "1. Describe the lobar distribution of mucus-plugs and its changes following therapy.",
    "2. Distinguish the independent contribution of lobar location from inter-participant variation in determining mucus plug physicochemical properties.",
    "3. Recognize the paradoxical relationship between baseline plug density and treatment response across lobar regions."
  ],
  "thu-0940": [
  "1. Understand the principles of 3D PREFUL MRI and its capabilities for assessing regional lung ventilation in patients with severe asthma.",
  "2. Interpret 3D PREFUL MRI outcomes in relation to conventional pulmonary function tests, including their advantages and limitations.",
  "3. Evaluate the potential of 3D PREFUL MRI as a tool for tracking disease progression and treatment response in severe asthma.",
  ],
  "thu-0950": [
    "1. State that in a real-world population of patients with asthma and/or COPD recruited from primary care, patients with COPD had reduced and more heterogeneous ventilation, greater acinar dimensions and lower gas transfer than patients with asthma patients, and to a lesser extent patients with asthma+COPD.",
    "2. Relate that patients with airways dysanapsis (abnormal FEV1/FVC with normal FEV1) had increased lung damage compared to age-matched patients with normal spirometry.",
    "3. Recognize that gas transfer and ventilation were reduced after 1 year, with greater changes in COPD than asthma, that acinar dimensions were the most significant predictor of TLco 1 year later, and FEV1/FVC was the most important predictor of VDP 1 year later.",
  ],
  "thu-1000": [
    "1. Understand how to quantify airway tree shape from non-contrast chest CT.",
    "2. Interpret the relationship between CT-derived airway tree shape features (elongation and flatness) and clinical characteristics of severe asthma.",
    "3. Appraise the potential role of airway tree shape features, compared with other CT-derived airway morphology measures, in predicting bronchoconstriction in patients with severe asthma.",
  ],
  "thu-1010": [
    "1. Demonstrate that sex-stratified and menstrual phase-controlled acquisition is necessary to uncover clinically relevant sex differences in asthma.",
    "2. Identify structure-function relationships that change throughout the menstrual cycle in women with asthma.",
    "3. Recognize that quantitative medical imaging is sensitive to these hormonal fluctuations.",
  ],
  "thu-1020": [
    "1. Describe different measurements for airflow obstruction using spirometry, oscillometry, and xenon MRI and how they are measured.",
    "2. Recognize differences in the prevalence of abnormal lung function when different measurements are used and be able to interpret overlaps between these groups.",
    "3. Evaluate the clinical significance of differences between participants with normal or abnormal FEV1 and abnormal VDP.",
  ],
  "thu-s2-1030": [
    "1.	Describe the limitations of spirometry-only diagnosis.",
    "2.	Articulate the rationale for incorporating pulmonary imaging measures into a multidimensional diagnostic schema for COPD.",
    "3.	Apply an integrated diagnostic framework incorporating pulmonary imaging to improve COPD classification and prognostication.",
  ],
  "thu-s2-1045": [
    "1.	Examine the impact of undiagnosed obstructive lung disease in the population",
    "2.	Review the detection of undiagnosed disease, using case-finding/screening will be reviewed.",
    "3.	Identify how patient's health outcomes are improved via these strategies."
  ],
  // COPD Paper Presentations
  "thu-1110": [
    "1. Summarize the principles of X-ray dark-field imaging.",
    "2. Describe how X-ray dark-field imaging visualizes pulmonary emphysema.",
    "3. Assess the feasibility of ultra-low-dose dark-field imaging.",
  ],
  "thu-1120": [
    "1. Describe the methodology for quantifying rib density from chest CT images, including automatic segmentation.",
    "2. Explain the role of CT-derived rib density as a predictor for longitudinal outcomes in COPD.",
    "3. Identify potential clinical applications of skeletal extra-pulmonary manifestations in COPD.",
  ],
  "thu-1130": [
    "1. Describe the principles of X-ray dark-field chest imaging and its sensitivity to pulmonary microstructure.",
    "2. Explain how radiomics features and machine-learning models can be utilized for thoracic imaging.",
    "3. Assess how texture metrics reflect the ability of dark-field radiomics to distinguish pulmonary emphysema from healthy lungs.",
  ],
  "thu-1140": [
    "1. Describe why small airways disease is difficult to detect non-invasively in early stages.",
    "2. List advantages and disadvantages for X-ray velocimetry for detection of small airways disease.",
    "3. Reflect on additional potential clinical indications for X-ray velocimetry for diagnosis of small airways disease.",
  ],
  "thu-1150": [
    "1. Explain how dark-field chest radiography detects microstructural lung changes in emphysema and pneumonia through small-angle X-ray scattering at alveolar air–tissue interfaces.",
    "2. Compare the diagnostic accuracy of combined dark-field and attenuation radiography versus conventional attenuation-only radiography for classifying pulmonary diseases.",
    "3. Identify the limitations of dark-field imaging in differentiating diseases with overlapping signal-loss patterns, such as emphysema and COVID-19 pneumonia.",
  ],
  "thu-1200": [
    "1. Explain the impact of oropharyngeal enlargement on PFTs in patients with obstructive lung disease.",
    "2. Discuss the unified respiratory structure-function relationship in patients with obstructive lung disease.",
    "3. Describe the quantitative imaging and computational modeling analysis for oropharyngeal metrics and qCT and CFD-derived airway resistance.",
  ],
  "thu-1210": [
    "1. Apply AI-based CT analysis to quantify body composition in COPD.",
    "2. Evaluate the agreement and diagnostic performance of CT-derived metrics against BIA.",
    "3. Use CT-derived FFMI for opportunistic identification of high-risk sarcopenia in COPD.",
  ],
  "thu-s3-1330": [
    "1.	Explain the basic methodology of Xenon MRI and what aspects of lung pathophysiology it can measure.",
    "2.	Describe how Xenon MRI is being used in the UK in a diagnostic setting.",
    "3.	Discuss future perspectives for multi-centre clinical adoption in clinical trials of new treatments."
  ],
  "thu-s3-1345": [
    "1. Understand the physiological information of SPECT ventilation scans.",
    "2. Know the differences compared to other imaging modalities, with respect to physiological information and implementation.",
    "3. Gain insights into its potential role in assessment of candidates for lung resection."
  ],  
  // Lung Cancer Paper Presentations
  "thu-1410": [
    "1. Describe the generation-dependent reduction in airway counts on CXR-dose CT compared with LCS-dose CT.",
    "2. Recognize the high agreement of automated airway diameter measurements between CXR-dose and LCS-dose CT across airway branch generations 0–5.",
    "3. Interpret which airway metrics remain reliable at ultra-low-dose CT and their potential use in quantitative airway evaluation.",
  ],
  "thu-1420": [
    "1. Explain the concept of bone attenuation contribution (BAC) in chest radiographs and its role in emerging functional X-ray imaging modalities such as dark-field imaging.",
    "2. Describe how synthetic chest radiographs and BAC maps can be generated from CT data and outline how these data can be used to train a U-Net model for pixel-level prediction of bone attenuation.",
    "3. Assess the effectiveness of machine learning–generated BAC maps for synthetic and real chest radiographs qualitatively and quantitatively with mean absolute percentage error and correlation performance metrics.",
  ],
  "thu-1455": [
    "1. Distinguish emphysema, PRM-defined small airway disease, and image-registration-derived CT biomarkers such as the Jacobian determinant.",
    "2. Explain how Jacobian metrics add mechanical information beyond attenuation-based imaging.",
    "3. Evaluate the independent clinical and prognostic value of integrated CT imaging biomarkers in COPD.",
  ],
  "thu-1505": [
    "1. Explain how masked variational autoencoders can be applied to generative anomaly detection in 129Xe MRI.",
    "2. Evaluate the role of spatial encodings and gated skip connections in maintaining anatomical fidelity during generating healthy-appearing ventilation slices.",
    "3. Interpret generative residual maps as a quantitative metric for characterizing regional ventilation defects.",
  ],
  "thu-1515": [
    "1. Quantify localized ipsilateral compensatory lung growth derived from longitudinal CT volumetry.",
    "2. Identify the association between impaired postoperative lung remodeling and the increased risk of tumor recurrence in stage I non-small cell lung cancer.",
    "3. Integrate volumetric prognostic markers into postoperative follow-up protocols to enhance the stratification of recurrence risk.",
  ],
  "thu-1525": [
    "1. Estimate the neccessity of new techniques to assess and monitor lung function after pediatric lung transplantation.",
    "2. Illustrate the principles of 3D PREFUL MRI to measure regional lung ventilation in free breathing without contrast agent administration.",
    "3. Assess the role 3D PREFUL MRI biomarkers in context of lung transplantation.",
  ],
  "thu-1535": [
    "1. Discuss the role of functional lung imaging with hyperpolarized 129Xe MRI to guide radiation therapy in lung cancer.",
    "2. Explain how functional avoidance mapping can potentially preserve lung regions at-risk for radiation induced lung injury.",
    "3. Describe the challenges of integrating a novel MRI technology into the radiation oncology clinical workflow.",
  ],
  // Rapid Fire Digital Poster Session
  "thu-1545": [
    "1. Summarize recent advances in pulmonary functional imaging across key disease areas, including COPD, asthma, lung cancer, interstitial lung disease (ILD)/fibrosis, and pulmonary vascular disease.",
    "2. Identify and compare emerging imaging techniques and quantitative biomarkers used for disease characterization, prognosis, and treatment monitoring in pulmonary disorders.",
    "3. Discuss current challenges and future opportunities in the clinical translation of pulmonary functional imaging research."
  ],
  "fri-s4-0745": [
    "1.	Understand how cariogenic oscillations in 129Xe-RBC signal relate to pulmonary vascular resistance.",
    "2.	Use 129Xe MRI to estimate capillary blood volume.",
    "3.	Understand how mapping the 129Xe-RBC chemical shift can visualize PH physiology."
  ],
  "fri-s4-0800": [
    "1.	Understand the importance of detailed phenotyping of patients with pulmonary hypertension",
    "2.	Describe the current role of imaging in the clinical assessment of pulmonary hypertension",
    "3.	Discuss emerging imaging applications in pulmonary hypertension"
  ],
  // Pulmonary Vascular Disease Paper Presentations
  "fri-0825": [
    "1. Describe the challenges and limitations of manual delineation of mismatched V/Q defects in planar scintigraphy, including inter-observer variability.",
    "2. Compare the performance of modern deep learning models (U-Net, nnU-Net, Swin UNETR, and BTU-Net) for automated segmentation of vascular perfusion defects.",
    "3. Interpret FROC-based evaluation metrics and assess the clinical potential of AI-driven segmentation tools for improving PE diagnosis and reporting.",
  ],
  "fri-0835": [
    "1. Explain the role of gas exchange dynamics in probing pulmonary physiology.",
    "2. Describe how dynamic information can be collected alongside static gas exchange images without sacrificing spatial resolution.",
    "3. Interpret differences in gas exchange between two exchange times in light of expected gas exchange physiology.",
  ],
  "fri-0845": [
    "1. Describe the challenges of large-scale annotation in V/Q scintigraphy and their impact on AI development for pulmonary embolism.",
    "2. Understand the design and implementation of AI-assisted annotation workflows to improve efficiency while maintaining quality.",
    "3. Recognize the value of multimodal datasets (e.g., V/Q imaging, CT, DVT status, D-dimer) for advancing research and clinical applications in PE diagnosis.",
  ],
  "fri-0855": [
    "1. Explain the physical principles of propagation-based synchrotron phase-contrast CT (PBI-CT), including the role of spatial coherence, monochromatic X-rays, extended propagation distances, and phase retrieval, and describe how these properties enable superior soft-tissue contrast and sub-100 µm spatial resolution compared to conventional attenuation-based MSCT.",
    "2. Evaluate the feasibility and diagnostic performance of PBI-CT for human-sized lung specimens at clinically relevant radiation doses.",
    "3. Assess the translational significance of extending PBI-CT from fixed porcine and human lung specimens to fresh, unfixed human lung tissue, and discuss how this progression informs the pathway toward future in vivo synchrotron-based clinical lung CT.",
  ],
  "fri-0905": [
    "1. Describe the key concepts and findings of the research.",
    "2. Compute the quantitative biomarker.",
    "3. Apply the research findings to larger cohorts.",
  ],
  "fri-0915": [
    "1. Understand the CaliFloret sequence and parameters which guided development.",
    "2. Develop a connection between research and clinical standard.",
    "3. Discuss future directions in xenon sequence/reconstruction development.",
  ],
  "fri-0925": [
    "1. Describe how CT measurements of the pulmonary vasculature are generated and categorized.",
    "2. Interpret the short-term longitudinal variability in CT-derived pulmonary vascular volume measurements over 16-weeks in patients with asthma receiving standard-of-care treatment.",
    "3. Assess the relationship between the changes in CT-derived pulmonary vascular volume measurements and the changes in clinical asthma outcomes over 16-weeks.",
  ],
  "fri-s5-1015": [
    "1.	Identify how optical coherence tomography is used bronchoscopically.",
    "2.	Understand how optical coherence tomography may be useful in early detection and diagnosis of lung disease.",
    "3.	Recognize how optical coherence tomography imaging may used over time to assess disease changes."
  ],
  "fri-s5-1030": [
    "1. Explain how imaging contributes to understanding the mechanisms underlying pulmonary fibrosis.",
    "2. Recognize the role of quantitative and longitudinal imaging in characterizing disease heterogeneity and progression.",
    "3. Understand how imaging-derived insights can inform clinical assessment and research in fibrotic lung disease."
  ],
  // ILD & Fibrosis Paper Presentations
  "fri-1055": [
    "1. Describe the independent associations of baseline fibrotic burden and emphysema extent with lung cancer risk in idiopathic pulmonary fibrosis (IPF).",
    "2. Explain how the association between fibrosis and lung cancer in IPF is influenced by both baseline disease extent and the subsequent pattern of fibrosis progression.",
    "3. Distinguish the clinical implications of fibrosis progression patterns in IPF.",
  ],
  "fri-1105": [
    "1. Discuss the benefits of an automated deep learning approach for vascular and airway segmentation, and identify its potential for longitudinal monitoring of disease progression and therapeutic testing in preclinical disease models.",
    "2. Evaluate the complementary roles of in vivo and ex vivo micro-CT segementation for providing insights into airway and vascular morphometry in the context of pulmonary disease.",
    "3. Describe the workflow for murine in vivo lung micro-CT imaging and preparation of insufflated murine lung samples for ex vivo micro-CT analysis.",
  ],
  "fri-1115": [
    "1. Explain the underlying measurement principles of PREFUL MRI and PRM, including their functional vs. structural focus.",
    "2. Compare and interpret PREFUL-, PRM-, and PFT-derived metrics in COPD.",
    "3. Recognize how acquisition conditions and measurement principles influence correlations and spatial agreement between imaging biomarkers.",
  ],
  "fri-1125": [
    "1. Recognize the limitations of conventional pulmonary function tests (PFTs) and 129Xe MRI ventilation defect percent (VDP) in detecting early-stage respiratory abnormalities in young, symptomatic individuals with a history of smoking or vaping.",
    "2. Describe the functionality of 129Xe MRI texture analysis.",
    "3. Understand the utility of 129Xe MRI texture features by examining their correlation with symptoms and their ability to differentiate smoking populations from non-smokers.",
  ],
  "fri-1135": [
    "1. Understand the potential role of AI-derived quantitative HRCT metrics as longitudinal imaging biomarkers for monitoring structural progression in usual interstitial pneumonia (UIP).",
    "2. Recognize differences in longitudinal HRCT progression patterns between UIP patients treated with antifibrotic therapy and those who remain untreated.",
    "3. Evaluate the relationship between serial AI-based quantitative HRCT changes and pulmonary function decline in routine clinical practice.",
  ],
  "fri-1145": [
    "1. Describe advanced ZTE-MRI techniques for lung imaging, and how they overcome traditional limitations of pulmonary MRI.",
    "2. Identify methods enabling lung imaging under free-breathing MRI.",
    "3. Identify potential clinical scenarios where these advanced techniques could be applied for patient care as a potential radiation-free alternative to conventional CT imaging.",
  ],
  "fri-1155": [
    "1. Evaluate how intensity-based, deformation-based and hybrid non-contrast CT ventilation methods discriminate between healthy, asthmatic and COPD cohorts.",
    "2. Compare the three methods at both whole-lung and lobar scale, and recognise that whole-lung averaging conceals the regional differences between them.",
    "3. Identify which ventilation biomarkers and which lung regions are most sensitive for severity stratification within obstructive lung disease."

    // "1. Evaluate the ability of non-contrast CT ventilation methods to discriminate between healthy, asthmatic, and COPD cohorts.",
    // "2. Compare the complementary strengths of intensity-based, deformation-based, and novel hybrid non-contrast CT ventilation methods for disease characterisation.",
    // "3. Identify which ventilation biomarkers are most sensitive for severity stratification within obstructive lung disease.",
  ],
  "fri-1450": [
    "1.	Describe the cardiovascular and thoracic effects of air pollution.",
    "2.	Identify the role of imaging in understanding myocardial and coronary effects of air pollution.",
    "3.	Implement actions to improve environmental sustainability in imaging and patient care."
  ],
  "fri-1325":[
    "1.	Develop their research projects from their own curiosity.",
    "2.	Approach research questions and complete them.",
    "3.	Enjoy, share, and contribute with the people around them and in the world."
  ],
  "fri-1345":[
    "1.	Describe the evolutionary biology of hibernation and fat storage for oxidative energy use (the sloth, hummingbird and grizzly bear).",
    "2.	Discuss the imaging tools available and how they impact our ability to predict diseases.",
    "3.	Explain the inflammation and its relationship to too much fat."
  ],
  // Young Investigator Forum Presentations
  "fri-1515": [
    "1. Describe how photon-counting CT enables simultaneous assessment of lung morphology, ventilation and perfusion from a single contrast-enhanced examination using spectral data and deformable image registration.",
    "2. Name expected reference ranges for photon-counting CT-derived pulmonary ventilation and perfusion in healthy adults.",
    "3. Apply regional ventilation and perfusion reference values to distinguish normal variability from potential pathology in clinical practice.",
  ],
  "fri-1525": [
    "1. Describe differences in CT mucus-plug volume, density, length, and lobar distribution between asthma and COPD.",
    "2. Identify the differential relationship between CT mucus-plug burden and spirometry in asthma versus COPD.",
    "3. Discuss how disease-specific differences in mucus-plug characteristics may reflect distinct underlying pathobiology in asthma versus COPD.",
  ],
  "fri-1535": [
    "1. Interpret how multimodal models can improve prognostic performance beyond conventional staging in NSCLC.",
    "2. Recognize that emerging biomarkers such as ctDNA may not retain independent significance when integrated into fully adjusted models.",
    "3. Assess how model design and variable selection influence the evaluation of prognostic biomarkers.",
  ],
  "fri-1545": [
    "1. Explain how pixel-wise lung thickness maps can be derived from chest radiographs using deep learning models trained on CT-based synthetic data.",
    "2. Evaluate the accuracy and limitations of total lung volume estimation from radiographs by interpreting performance metrics such as MSE and correlation with CT reference standards.",
    "3. Apply lung thickness–based structural information to improve normalization and regional interpretation in pulmonary functional imaging modalities.",
  ],
  "fri-1555": [
    "1. Describe how different 129Xe MRI VDP quantification methods compare in their ability to predict postoperative complications following lung resection surgery.",
    "2. Explain the potential relevance of 129Xe MRI VHI as a complementary imaging biomarker, particularly in patients with preserved lung function who are otherwise considered low surgical risk.",
    "3. Evaluate the incremental predictive value of 129Xe MRI-derived measures over standard clinical preoperative variables in surgical risk stratification.",
  ]
};

/**
 * PEOPLE DIRECTORY (PLACEHOLDERS)
 * - Moderators: name + picture + credentials
 * - Speakers:   name + picture + credentials + bio
 */
const PEOPLE = {
  // Moderators (placeholders)
  "sean-fain": {
    id: "sean-fain",
    name: "Sean Fain, Ph.D",
    role: "moderator",
    credentials: ["Professor, Vice Chair of Research",
      "Department of Radiology",
      "Affiliate Appointments Biomedical Engineering",
      "Electrical and Computer Engineering, and Human Physiology",
      "Fellow of the ISMRM and Distinguished Investigator,",
      "Academy for Radiology & Biomedical Imaging Research",
    ],
    photoUrl: "images/Fain_Sean.jpg",
  },
  "sarah-svenningsen": {
    id: "sarah-svenningsen",
    name: "Sarah Svenningsen, Ph.D",
    role: "moderator",
    credentials: [
      "Canada Research Chair in Translational Pulmonary Imaging (Tier 2)",
      "Assistant Professor of Medicine, McMaster University",
      "Affiliate Scientist, Research Institute of St. Joe's Hamilton",
      "Firestone Institute for Respiratory Health & Imaging Research Centre",
      "St. Joseph's Healthcare Hamilton",
    ],
    photoUrl: "images/Svenningsen_Sarah.jpg",
  },
  "miranda-kirby": {
    id: "miranda-kirby",
    name: "Miranda Kirby, Ph.D",
    role: "moderator",
    credentials: [
      "Canada Research Chair, Tier 2 in Quantitative Imaging",
      "Associate Professor, Department of Physics",
      "Toronto Metropolitan University",
    ],
    photoUrl: "images/Kirby_Miranda.jpg",
  },
  "benjamin-smith": {
    id: "benjamin-smith",
    name: "Benjamin M Smith, MD, MSc, FRCPC",
    role: "moderator",
    credentials: [
      "Associate Professor, Faculty of Medicine and Health Sciences,",
      "McGill University",
      "Scientist and Director, Centre for Outcomes Research and",
      "Evaluation, McGill University Health Centre Research Institute",
      "Director, Division of Clinical Epidemiology, Department of",
      "Medicine, McGill University",
      "Respiratory Physician, McGill University Health Centre",
    ],
    photoUrl: "images/Smith_Benjamin.jpg",
  },
  "laura-walkup": {
    id: "laura-walkup",
    name: "Laura Walkup, PhD",
    role: "moderator",
    credentials: [
      "Associate Professor",
      "Cincinnati Children's Hospital Medical Center"
    ],
    photoUrl: "images/Walkup_Laura.jpg",
  },
  "yasutaka-nakano": {
    id: "yasutaka-nakano",
    name: "Yasutaka Nakano, MD. PhD",
    role: "moderator",
    credentials: [
      "Professor",
      "Department of Respiratory Medicine",
      "Shiga University of Medical Science",
      "Otsu, Japan"
    ],
    photoUrl: "images/Nakano_Yasutaka.png",
  },
  "helen-marshall": {
    id: "helen-marshall",
    name: "Helen Marshall, PhD",
    role: "moderator",
    credentials: [
      "Research Staff Scientist for Clinical Translation of Lung MRI",
      "Imaging Theme, Division of Clinical Medicine, School of Medicine & Population Health",
      "The University of Sheffield",
    ],
    photoUrl: "images/Marshall_Helen.jpg",
  },
  "k-yeol-lee": {
    id: "k-yeol-lee",
    name: "Ki Yeol Lee, MD, PhD",
    role: "moderator",
    credentials: [
      "Professor of Radiology",
      "Korea University Guro Hospital",
      "Seoul, Republic of Korea",
      "Specialty: Thoracic Imaging, Pulmonary Embolism, COPD",
    ],
    photoUrl: "images/Lee_Ki.jpg",
  },
  "joon-beom-seo": {
    id: "joon-beom-seo",
    name: "Joon Beom Seo, MD, PhD",
    role: "moderator",
    credentials: [
      "Professor of Radiology",
      "University of Ulsan College of Medicine and Asan Medical Center",
      "Seoul, Korea",
    ],
    photoUrl: "images/Seo_Joon.jpg",
  },
  "ho-yun-lee": {
    id: "ho-yun-lee",
    name: "Ho Yun Lee, MD, PhD",
    role: "moderator",
    credentials: [
      "Professor, Department of Radiology",
      "Samsung Medical Center, Sungkyunkwan University School of Medicine",
      "Seoul, Korea",
    ],
    photoUrl: "images/Lee_Ho Yun.jpg",
  },
   "edwin-van-beek": {
    id: "edwin-van-beek",
    name: "Edwin J.R. van Beek, MD, PhD, Med, FISMRM, EBCR, FRCPE, FRCR",
    role: "moderator",
    credentials: [
      "Chair of Clinical Radiology",
      "Director Edinburgh Imaging Queen’s Medical Research Institute",
      "Honorary Consultant Cardiovascular Radiologist, NHS Lothian",
      "Institute for Neuroscience and Cardiovascular Research",
      "University of Edinburgh",
    ],
    photoUrl: "images/Beek_Edwin.jpg",
  },
  "talissa-altes": {
    id: "talissa-altes",
    name: "Talissa Altes, MD",
    role: "moderator",
    credentials: [
      "Radiology Chair",
      "Professor of Radiology",
      "Gwilym S. and Maria Antonia Lodwick Distinguished Professorship in Radiology",
    ],
    photoUrl: "images/Altes_Talissa.png",
  },
  "sang-lee": {
    id: "sang-lee",
    name: "Sang Min Lee, M.D., Ph.D",
    role: "moderator",
    credentials: [
      "Professor of Radiology",
      "University of  Ulsan College of Medicine",
      "Division of Cardiothoracic Radiology",
      "Department of Radiology, Asan Medical Center"
    ],
    photoUrl: "images/",
  },

  // Speakers (placeholders)
  "param-nair": {
    id: "param-nair",
    name: "Parameswaran Nair, MD, PhD, FRCP, FRCPC, FCAHS",
    role: "speaker",
    credentials: [
      "Frederick E. Hargreave Teva Innovation Chair in Airway Diseases",
      "Professor of Medicine, McMaster University",
      "Staff Respirologist, St Joseph's Healthcare",
      "Hamilton, Ontario, Canada",
    ],
    photoUrl: "images/Nair_P.jpg",
    bio: `Dr Nair graduated in 1989 from the University of Kerala Medical College in Trivandrum, India. He is currently the 
    Frederick E. Hargreave Teva Innovation Chair in Airway Diseases & Professor of Medicine in the Division of Respirology 
    at McMaster University in Hamilton, Ontario, Canada, providing tertiary care to patients with severe asthma and other 
    complex airway and eosinophilic lung diseases. He directs a patient-centred translational research program at St Joseph’s 
    Healthcare Hamilton, focussed on charactering bronchitis using sputum biomarkers, bio-imaging, and targeted therapy 
    with biologics and small molecule antagonists. His laboratory has contributed to over 375 peer-reviewed publications 
    in high impact general medical, allergy, and respiratory journals (h-index 85, >31,000 citations), and has been 
    recognized by a Canada Research Chair, fellowships of the Canadian Academy of Health Sciences, the European 
    Respiratory Society, the American Thoracic Society, the American College of Chest Physicians, and inducted 
    memberships of the Collegium Internationale Allergologicum and the National Academy of Medical Sciences of India.`,
  },
  "mario-castro": {
    id: "mario-castro",
    name: "Mario Castro, MD, MPH",
    role: "speaker",
    credentials: [
      "L. E. Phillips and Lenora Carr Phillips Professor",
      "Chief, Pulmonary, Critical Care and Sleep Medicine",
      "Vice Chair for Clinical and Translational Research",
      "Director, Frontiers Clinical and Translational Science Institute at the University of Kansas",
      "University of Kansas School of Medicine",
    ],
    photoUrl: "images/Castro_Mario.jpg",
    bio: `Dr. Castro is the L. E. Phillips and Lenora Carr Phillips Professor of Medicine, Vice Chair for Clinical and Translational Research, Department of Medicine, Chief, 
    Division of Pulmonary, Critical Care and Sleep Medicine at University of Kansas Medical Center (KUMC), and Director of the Frontiers: 
    University of Kansas Clinical and Translational Science Institute and Adjunct Research Professor at the University of 
    Missouri-Kansas City School of Medicine. He has been a leader of the CDC Controlling Asthma in American Cities, 
    American Lung Association Airway Clinical Research Centers, and NHLBI Asthma Clinical Research Network, Severe Asthma 
    Research Program, AsthmaNet and PrecISE research networks. His translational research projects in the Asthma and Airway 
    Translational Research Unit (AATRU) are focused on the pathogenesis of asthma and treatment of asthma, especially 
    in severe disease. Dr. Castro has spearheaded several initiatives to combat COVID-19 and its sequelae: NIH/NIAID-funded 
    COVID-19 Prevention Trials Network (CoVPN trial), ACTIV-2 trial, PCORNET HERO HCQ and the RECOVER: Multi-site Observational 
    Study of Post-Acute Sequelae of SARS-CoV-2 Infection trial. He is the author of over 30 chapters/books and over 500 peer-reviewed 
    articles; over the past 30+ years, Dr. Castro has trained over 70 junior clinical & translational scholars. He is widely recognized 
    as an expert clinician, an excellent mentor, and skilled educator with a significant commitment to the development of young 
    investigators’ careers.`,
  },
  "surya-bhatt": {
    id: "surya-bhatt",
    name: "Surya P Bhatt, MD, MSPH",
    role: "speaker",
    credentials: [
      "Professor of Medicine",
      "Endowed Professor of Airways Disease",
      "Director, Center for Lung Analytics and Imaging Research (CLAIR)",
      "Medical Director, Pulmonary Function & Exercise Physiology Lab",
      "Division of Pulmonary, Allergy and Critical Care Medicine",
      "University of Alabama at Birmingham",
    ],
    photoUrl: "images/Bhatt_Surya.JPG",
    bio: `Dr. Bhatt is Professor of Medicine and Endowed Professor of Airways Disease at the University of Alabama at Birmingham. He is the Director 
    of the UAB Center for Lung Analytics and Imaging Research, and also serves as the Medical Director of the UAB Pulmonary Function Testing and Exercise 
    Physiology Lab. His primary clinical and research interests are in COPD. He leads two research programs: lung imaging and COPD clinical trials. His 
    research is funded by the NHLBI with R and U grants. He has developed several new imaging biomarkers for COPD as well as new spirometric methods to 
    detect airflow obstruction. He is the overall PI of the multicenter TeleCOPD study, funded by the NHLBI. He has more than 285 peer-reviewed publications, 
    including in high impact journals such as NEJM, JAMA, and JCI. He is an elected member of the American Society for Clinical Investigation (ASCI). He has 
    received multiple national and international awards for his research, including the Alfred Soffer Award from the ACCP, the UAB Dean’s Excellence Award for 
    Research, Early Career Achievement Award from the Clinical Problems Assembly of the American Thoracic Society, the Dr. Linda Hall Innovation Award from the 
    American Association of Cardiovascular and Pulmonary Rehabilitation, the Top Reviewer Award from both the AJRCCM and the Annals ATS journals, and most 
    recently was elected member of the American Society of Clinical Investigation (ASCI). He has authored multiple book chapters including in Braunwald’s Heart 
    Disease and Murray and Nadel’s Textbook of Respiratory Medicine. He serves as Associate Editor for the Annals of ATS and on the Editorial Board of the 
    American Journal of Respiratory and Critical Care Medicine.`,
  },
  "shawn-aaron": {
    id: "shawn-aaron",
    name: "Shawn Aaron, MD, FRCPC",
    role: "speaker",
    credentials: [
      "Professor, Department of Medicine, The University of Ottawa",
      "Respirologist, The Ottawa Hospital",
      "Senior Scientist, The Ottawa Hospital Research Institute",
    ],
    photoUrl: "images/Aaron_Shawn.jpg",
    bio: `Dr. Shawn Aaron is a Professor in The Department of Medicine, The University of Ottawa and a Senior Scientist at the Ottawa Hospital Research Institute. 
    He is a respirologist at The Ottawa Hospital. Dr. Aaron conducts clinical and health services research focused in 3 respiratory diseases: COPD, asthma and cystic 
    fibrosis.`,
  },
  "jim-wild": {
    id: "jim-wild",
    name: "Jim Wild, PhD",
    role: "speaker",
    credentials: [
      "Professor of Magnetic Resonance Physics",
      "POLARIS and Insigneo Institute ",
      "University of Sheffield, UK"
    ],
    photoUrl: "images/Wild_Jim.png",
    bio: `Jim is a Magnetic Resonance Physicist/Engineeer whose own research is focused on the technology and applications of hyperpolarised gas and lung Magnetic Resonance Imaging (MRI). 
    He works closely with clinicians and industrial collaborators on translating technologies for healthcare benefit. He was an EPSRC advanced research fellow and he was the UKs first 
    non-clinical NIHR research professor. He currently holds an EPSRC prosperity partnership with GE Healthcare that builds on long standing academic-industrial collaborations around MRI 
    technology and its translation. In 2025 Jim was awarded the Institute of Physics Peter Mansfield Medal for distinguished contributions to medical physics. `,
  },
  "greg-king-speaker": {
    id: "greg-king-speaker",
    name: "Greg King, MBChB (Otago), FRACP, PhD, FAPSR",
    role: "speaker",
    credentials: [
      "Department of Respiratory Medicine",
      "Royal North Shore Hospital",
      "The Woolcock Institute of Medical Research, Macquarie University",
      "The Northern Clinical School, Sydney University",
    ],
    photoUrl: "images/King_Greg.jpg",
    bio: `Dr Greg King is a respiratory physician at the Royal North Shore Hospital, The Woolcock Institute of Medical Research and Northern Clinical School, University 
    of Sydney. He has a research and clinical practice interest and focus in asthma, COPD and bronchiolitis obliterans in haemopoietic stem cell transplant. His research 
    involves complex lung function and imaging to measure physiology that is relevant to disease mechanisms and characterisation, to better understand, manage and perhaps 
    one day, prevent and cure airways diseases.`,
  },
  "kn-bastiaan-driehuys": {
    id: "kn-bastiaan-driehuys",
    name: "Bastiaan Driehuys, Ph.D",
    role: "speaker",
    credentials: [
      "Professor of Radiology",
      "Duke University Medical Center",
    ],
    photoUrl: "images/Driehuys_Bastiaan.jpg",
    bio: `Bastiaan Driehuys is a professor of radiology at Duke University. His research focuses on the physics, engineering and applications of MRI using hyperpolarized 129Xe gas. 
    His team is focused on exploiting the unique properties of this technology for non-invasive 3D imaging of pulmonary function. His current interests are in imaging pulmonary gas 
    exchange, hemodynamics and oxygenation in patients with interstitial lung disease and pulmonary vascular disease. Dr. Driehuys holds a B.A. in physics from Franklin & Marshall 
    College (1990) and a Ph.D. in atomic physics from Princeton University (1995). Prior to his faculty role at Duke, he held senior R&D positions at Amersham Health (now GE Healthcare). 
    In addition to his academic role, he serves as Chief Scientific Officer for Polarean Imaging, a company he founded to commercialize hyperpolarized 129Xe MRI technology. In 2025 he 
    received the American Thoracic Society Research Innovation and Translation Award for his work in advancing xenon MRI.`,
  },
  "david-kiely": {
    id: "david-kiely",
    name: "David Kiely, MD, FRCP",
    role: "speaker",
    credentials: [
      "Professor of Pulmonary Vascular Medicine",
      "Director, Sheffield Pulmonary Vascular Disease Unit",
      "Sheffield Teaching Hospitals NHS",
      "Sheffield, UK",
    ],
    photoUrl: "images/Kiely_David.png",
    bio: `David is a consultant Pulmonologist and a Professor of Pulmonary Vascular Medicine at the University of Sheffield. He was appointed as the Director of the Sheffield Pulmonary 
    Vascular Disease Unit, one of the largest pulmonary hypertension centres in the world, in 2001. David is the Chief Investigator of the ASPIRE Registry, a PVRI Imaging Task Force 
    Leader and member of the 2022 ESC/ERS PH Guidelines and 7th WSPH Imaging Task Force. His research encompasses the diagnosis and assessment of pulmonary hypertension with a focus on 
    imaging. He has participated in research funded by the NIHR, BHF, Wellcome Trust and MRC.`,
  },
  "lida-hariri": {
    id: "lida-hariri",
    name: "Lida Hariri, MD, PhD",
    role: "speaker",
    credentials: [
      "Associate Professor",
      "Department of Pathology",
      "Division of Pulmonary and Critical Care Medicine",
      "Massachusetts General Hospital, Harvard Medical School",
    ],
    photoUrl: "images/Hariri_Lida.jpg",
    bio: `Dr. Lida Hariri is a physician-scientist and Associate Professor at Massachusetts General Hospital (MGH), Harvard Medical School. She received her combined MD/PhD 
    degrees from the University of Arizona, with her doctorate in Biomedical Engineering focused on multimodal optical imaging for early cancer detection. She subsequently 
    completed her pathology residency and fellowship training at MGH, where she is a practicing pulmonary pathologist. Dr. Hariri leads an NIH-funded research lab which 
    focuses on diagnosis, monitoring, and pathophysiologic investigation of fibrotic interstitial lung disease, interstitial lung abnormalities, long COVID, small airways 
    disease, and vaping associated lung disease using novel techniques such as endobronchial optical coherence tomography (EB-OCT). She is an elected member of the Fleischner 
    Society and the American Society for Clinical Investigation, and serves as co-chair and standing member on NIH study section.`,
  },
  "joaee-choe": {
    id: "joaee-choe",
    name: "Jooae Choe, MD, PhD",
    role: "speaker",
    credentials: [
      "Assistant Professor of Radiology",
      "Asan Medical Center, University of Ulsan College of Medicine",
      "Seoul, Republic of Korea",
    ],
    photoUrl: "images/Choe_Jooae.JPG",
    bio: `Jooae Choe, MD, PhD, is an Assistant Professor of Radiology at Asan Medical Center, University of Ulsan College of Medicine, Seoul, Korea. She is a thoracic
     radiologist whose research focuses on quantitative chest CT and imaging biomarkers to characterize fibrotic lung disease. Her work aims to improve understanding 
     of fibrosis mechanisms, disease progression, and risk stratification through longitudinal imaging analysis and artificial intelligence–based approaches, with the 
     goal of advancing clinically meaningful monitoring and outcome prediction in interstitial lung diseases`,
  },
  "kate-hanneman": {
    id: "kate-hanneman",
    name: "Kate Hanneman, MD, MPH, FRCPC",
    role: "speaker",
    credentials: [
      "Associate Professor and Vice Chair Research",
      "Department of Medical Imaging, University of Toronto",
      "Cardiac Radiologist, Joint Department of Medical Imaging",
    ],
    photoUrl: "images/Hanneman_Kate (1).jpg",
    bio: `Kate Hanneman MD MPH, is an Associate Professor and Vice Chair of Research at the University of Toronto and a Clinician Scientist at the Toronto General Hospital 
    Research Institute. She completed medical school and diagnostic radiology residency at the University of Toronto, a cardiovascular imaging fellowship at Stanford University, 
    and a Masters in Public Health in Epidemiology at the Harvard TH Chan School of Public Health. She leads an active research program focused on environmental sustainability 
    and improving health outcomes for patients using cardiac imaging. She has published over 200 peer-reviewed articles and has received more than $7 million in peer-reviewed 
    grant funding. She holds multiple leadership roles, including Associate Editor with Radiology, Radiology: Cardiothoracic Imaging, the Journal of Cardiovascular Magnetic Resonance, 
    and the Canadian Association of Radiologists Journal. She is Chair of the RSNA Annual Meeting Program Planning Committee and Environmental Sustainability Committee, Co-Chair of the 
    Canadian Association of Radiologists Sustainability Working Group, Chair of the International Society for Magnetic Resonance in Medicine Environmental Sustainability Advisory 
    Committee, and Deputy Lead for Sustainability at the Joint Department of Medical Imaging and University Medical Imaging Toronto. Her research related to sustainability evaluates 
    the impact of mitigation strategies to improve the sustainable delivery of medical imaging and the use of cardiac imaging to understand the health effects of climate and environmental 
    exposures.`,
  },
  "hiroto-hatabu": {
    id: "hiroto-hatabu",
    name: "Hiroto Hatabu, MD, PhD, FACR, FJCR",
    role: "speaker",
    credentials: [
      "Chief, Center for Clinical Imaging Innovation (CCII)",
      "Vice Chair for Clinical Imaging Innovation, Professor of Radiology",
      "Hospital of the University of Pennsylvania",
      "President, The Fleischner Society",
      "Former and Inaugural Chair of the Board, International Workshop for Pulmonary Functional Imaging (IWPFI)",
      "Professor of Radiology Emeritus, Harvard Medical School",
      "Clinical Investigator, Brigham and Women’s Hospital"
    ],
    photoUrl: "/images/Hatabu_Hiroto.JPG",
    bio: `Dr. Hatabu serves as Professor of Radiology at Hospital of the University of Pennsylvania and Vice Chair for Clinical Imaging Innovation, Chief, Center for Clinical Imaging Innovation (CCII), 
    and Professor Emeritus at Harvard Medical School. He was awarded The W S Moore Award for Clinical Science from the ISMRM and The Fleischner Young Investigator Award from Beth Israel Hospital Boston 
    in 1996. He has contributed to over 470 peer-reviewed manuscripts and has made a myriad of important findings in MRI, CT and diffuse lung disease. He has served as President of the Fleischner 
    Society (June 2025-June 2026) and is a former and inaugural Chair of the Board, International Workshop for Pulmonary Functional Imaging (IWPFI)`,
  },
  "schiebler-mark": {
    id: "schiebler-mark",
    name: "Mark L. Schiebler, M.D.",
    role: "speaker",
    credentials: [
      "Provisional Senior Consultant",
      "Cardiothoracic Radiology, Mayo Clinic",
      "Jacksonville, FL",
      "Professor Emeritus",
      "Department of Radiology",
      "UW Health School of Medicine and Public Health",
    ],
    photoUrl: "images/Schiebler_Mark.jpg",
    bio: `Dr. Schiebler is a senior Cardiothoracic Radiologist that has had a long-time interest in Asthma CT and the imaging of muscle tissue and how this impacts type 2 inflammation. 
    He enjoys cooking, hiking, travel and making digital art.`,
  },
 "sniderman-andrew": {
  id: "sniderman-andrew",
  name: "Andrew Stobo Sniderman",
  role: "speaker",
  credentials: null,
  hideBioTitle: true,
  photoUrl: "images/sniderman_andrew.jpg",
  bio: `Andrew Stobo Sniderman is a writer, lawyer and Rhodes Scholar from Montreal. He has written for the New York Times, the Globe and Mail and Maclean's. He has also argued before the Supreme Court of Canada, served as the human rights policy advisor to the Canadian minister of foreign affairs, and worked for a judge of South Africa's Constitutional Court.\n\nHe will share stories from his acclaimed book, [Valley of the Birdtail: An Indian Reserve, a White Town, and the Road to Reconciliation](https://www.harpercollins.ca/9781443466301/valley-of-the-birdtail/). This book follows multiple generations of two families, one white and one Indigenous, and weaves their lives into the larger story of Canada. Valley of the Birdtail is about how two communities became separate and unequal—and what it means for the rest of us.`,
},
 
};

function PersonLink({ personId, onOpen, children }) {
  return (
    <button
      type="button"
      className="program-person-link"
      onClick={() => onOpen(personId)}
    >
      {children}
    </button>
  );
}

function LearningObjectivesLink({ onOpen, sessionId }) {
  return (
    <button
      type="button"
      className="program-learning-link"
      onClick={() => onOpen(sessionId)}
    >
      Learning objectives
    </button>
  );
}

function LearningObjectivesModal({ objectives, onClose, sessionTitle }) {
  useEffect(() => {
    if (!objectives) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [objectives, onClose]);

  if (!objectives) return null;

  return (
    <div
      className="program-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Learning objectives"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="program-modal">
        <button
          type="button"
          className="program-modal-close"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          ✕
        </button>

        {/* <div className="program-modal-header">
          <div className="program-modal-titleblock"> */}
            {sessionTitle ? (
                <div className="lo-modal-title">{sessionTitle}</div>
              ) : null}
            {/* <div className="lo-modal-subtitle">Learning Objectives</div> */}

            
          {/* </div>
        </div> */}

        <div className="program-modal-body">
          <p className="lo-modal-intro">
            At the end of this session, participants will be able to:
          </p>

          <ol className="lo-modal-list">
            {objectives.map((obj, idx) => (
              <li key={idx}>{obj}</li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}


// function PersonModal({ person, onClose }) {
//   useEffect(() => {
//     if (!person) return;

//     const onKeyDown = (e) => {
//       if (e.key === "Escape") onClose();
//     };

//     const prevOverflow = document.body.style.overflow;
//     document.body.style.overflow = "hidden";

//     window.addEventListener("keydown", onKeyDown);
//     return () => {
//       window.removeEventListener("keydown", onKeyDown);
//       document.body.style.overflow = prevOverflow;
//     };
//   }, [person, onClose]);

//   if (!person) return null;

//   const hasRealPhoto =
//     person.photoUrl &&
//     typeof person.photoUrl === "string" &&
//     !person.photoUrl.startsWith("PLACEHOLDER");

//   const showBio = person.role === "speaker" && !!person.bio;

//   return (
//     <div
//       className="program-modal-overlay"
//       role="dialog"
//       aria-modal="true"
//       aria-label={`${person.name} details`}
//       onMouseDown={(e) => {
//         if (e.target === e.currentTarget) onClose();
//       }}
//     >
//       <div className="program-modal">
//         {/* Close button */}
//         <button
//           type="button"
//           className="program-modal-close"
//           onClick={onClose}
//           aria-label="Close"
//           title="Close"
//         >
//           ✕
//         </button>

//         <div className="program-modal-header">
//           <div className="program-modal-photo">
//             {hasRealPhoto ? (
//               <img src={person.photoUrl} alt={person.name} />
//             ) : (
//               <div className="program-modal-photo-fallback" aria-hidden="true">
//                 {person?.name?.trim()?.[0] ?? "?"}
//               </div>
//             )}
//           </div>

//           <div className="program-modal-titleblock">
//             <div className="program-modal-name">{person.name}</div>
//             {person.credentials && (
//               <div className="program-modal-cred">
//                 {Array.isArray(person.credentials)
//                   ? person.credentials.map((line, idx) => (
//                       <div key={idx}>{line}</div>
//                     ))
//                   : person.credentials}
//               </div>
//             )}
//             <div className="program-modal-role">
//               {person.role === "speaker" ? "Speaker" : "Moderator"}
//             </div>
//           </div>
//         </div>

//         {showBio && (
//           <div className="program-modal-body">
//             <div className="program-modal-section-title">Bio</div>
//             <div className="program-modal-bio">{person.bio}</div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }
function parseBioWithLinks(bio) {
  if (!bio) return "";
  return bio.replace(
  /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
  '<a href="$2" target="_blank" rel="noopener noreferrer" class="program-modal-bio-link">$1</a>'
);
}

function PersonModal({ person, onClose }) {
  useEffect(() => {
    if (!person) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [person, onClose]);

  if (!person) return null;

  const hasRealPhoto =
    person.photoUrl &&
    typeof person.photoUrl === "string" &&
    !person.photoUrl.startsWith("PLACEHOLDER");

  const showBio = person.role === "speaker" && !!person.bio;

  return (
    <div
      className="program-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${person.name} details`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="program-modal">
        <button
          type="button"
          className="program-modal-close"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          ✕
        </button>

        <div className="program-modal-header">
          <div className="program-modal-photo">
            {hasRealPhoto ? (
              <img src={person.photoUrl} alt={person.name} />
            ) : (
              <div className="program-modal-photo-fallback" aria-hidden="true">
                {person?.name?.trim()?.[0] ?? "?"}
              </div>
            )}
          </div>

          <div className="program-modal-titleblock">
            <div className="program-modal-name">{person.name}</div>
            {person.credentials && (
              <div className="program-modal-cred">
                {Array.isArray(person.credentials)
                  ? person.credentials.map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))
                  : person.credentials}
              </div>
            )}
            <div className="program-modal-role">
              {person.role === "speaker" ? "Speaker" : "Moderator"}
            </div>
          </div>
        </div>

        {showBio && (
          <div className="program-modal-body">
            {!person.hideBioTitle && (
              <div className="program-modal-section-title">Bio</div>
            )}
            <div
              className="program-modal-bio"
              dangerouslySetInnerHTML={{ __html: parseBioWithLinks(person.bio) }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Filter keys
const FILTERS = {
  ALL: "all",
  AT_A_GLANCE: "at-a-glance",
  WED: "wednesday",
  THU: "thursday",
  FRI: "friday",
};

const Program = () => {
  useEffect(() => {
    document.title = "IWPFI2026_Program";
  }, []);
  const [openPersonId, setOpenPersonId] = useState(null);
  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL);

  // NEW: learning objectives modal state
  const [openObjectivesSessionId, setOpenObjectivesSessionId] = useState(null);
  const [openObjectivesSessionTitle, setOpenObjectivesSessionTitle] = useState("");

  const peopleMap = useMemo(() => PEOPLE, []);
  const openPerson = openPersonId ? peopleMap[openPersonId] : null;

  const openObjectives =
    openObjectivesSessionId ? LEARNING_OBJECTIVES[openObjectivesSessionId] : null;

  // Load filter from URL hash on first mount (e.g., /program#wednesday)
  useEffect(() => {
    const hash = (window.location.hash || "").replace("#", "").trim();
    const allowed = new Set(Object.values(FILTERS));
    if (allowed.has(hash)) {
      setActiveFilter(hash);
    }
  }, []);

  const setFilter = (filterKey) => {
    setActiveFilter(filterKey);

    // Update URL hash so this view can be shared/bookmarked
    if (filterKey === FILTERS.ALL) {
      window.history.replaceState(null, "", window.location.pathname);
    } else {
      window.history.replaceState(null, "", `#${filterKey}`);
    }

    // Scroll to top of program content
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (e, filterKey) => {
    e.preventDefault();
    setFilter(filterKey);
  };

  const openModalFor = (personId) => {
    if (!peopleMap[personId]) {
      console.warn(`Missing PEOPLE entry for id: ${personId}`);
      return;
    }
    setOpenPersonId(personId);
  };

  const openObjectivesFor = (sessionId, sessionTitle) => {
    if (!LEARNING_OBJECTIVES[sessionId]) {
      console.warn(`Missing LEARNING_OBJECTIVES entry for session id: ${sessionId}`);
      return;
    }
    setOpenObjectivesSessionId(sessionId);
    setOpenObjectivesSessionTitle(sessionTitle || "");
  };

  // const closeModal = () => setOpenPersonId(null);

  // const showAtAGlance =
  //   activeFilter === FILTERS.ALL || activeFilter === FILTERS.AT_A_GLANCE;
  const showWed = activeFilter === FILTERS.ALL || activeFilter === FILTERS.WED;
  const showThu = activeFilter === FILTERS.ALL || activeFilter === FILTERS.THU;
  const showFri = activeFilter === FILTERS.ALL || activeFilter === FILTERS.FRI;
  const showSat = activeFilter === FILTERS.ALL || activeFilter === FILTERS.SAT;


  return (
    <div id="everything">
      <NavigationBar />

      <main className="page-program">
        {/* Page title */}
        <section className="program-section">
          <h1 className="program-title">Program</h1>
          <p className="program-intro">
            The following preliminary program outlines the scientific sessions,
            keynotes, and events for IWPFI 2026. Minor changes may occur as the
            program is finalized.
          </p>

          {/* In-page navigation links -> now filters */}
          <nav className="program-nav" aria-label="Program filters">
            {/* <a
              href="#at-a-glance"
              onClick={(e) => handleNavClick(e, FILTERS.AT_A_GLANCE)}
            >
              Program-at-a-glance
            </a> */}
            <a
              href="#wednesday"
              onClick={(e) => handleNavClick(e, FILTERS.WED)}
            >
              Wednesday, September 23
            </a>
            <a
              href="#thursday"
              onClick={(e) => handleNavClick(e, FILTERS.THU)}
            >
              Thursday, September 24
            </a>
            <a
              href="#friday"
              onClick={(e) => handleNavClick(e, FILTERS.FRI)}
            >
              Friday, September 25
            </a>
            <a
              href="#saturday"
              onClick={(e) => handleNavClick(e, FILTERS.SAT)}
            >
              Saturday, September 26
            </a>

            {/* Show full program only when filtered */}
            {activeFilter !== FILTERS.ALL && (
              <a href="#all" onClick={(e) => handleNavClick(e, FILTERS.ALL)}>
                Show full program
              </a>
            )}
          </nav>
        </section>

        {/* Program-at-a-glance */}
        {/* {showAtAGlance && (
          <section id="at-a-glance" className="program-section">
            <p className="program-day-note">Program-at-a-glance</p>

            

            <div className="program-table-wrapper">
              <table className="program-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Key Activities</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Wednesday, September 23, 2026</td>
                    <td>
                      Arrival and registration; Welcome Reception (Terrasse
                      Après-Ski, 5:00–7:00 pm)
                    </td>
                  </tr>
                  <tr>
                    <td>Thursday, September 24, 2026</td>
                    <td>
                      Session 1 – Asthma; Session 2 – COPD; Session 3 – Lung
                      Cancer; Digital Poster Presentations I; Outdoor activities
                      (optional); Free time
                    </td>
                  </tr>
                  <tr>
                    <td>Friday, September 25, 2026</td>
                    <td>
                      Session 4 – Pulmonary Vascular Disease; Session 5 – ILD and
                      Fibrosis; John West Medalists; Young Investigator Forum;
                      Digital Poster Presentations II; Gala and Lecture/YI Awards
                    </td>
                  </tr>
                  <tr>
                    <td>Saturday, September 26, 2026</td>
                    <td>Board Meeting Breakfast; Checkout</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )} */}

        {/* Wednesday */}
        {/* Optional: print button only when viewing the one-pager */}
            {/* {activeFilter === FILTERS.ALL && (
              <div className="no-print" style={{ margin: "10px 0 14px" }}>
                <button
                  type="button"
                  className="reg-button"
                  onClick={() => window.print()}
                >
                  🖨️ Print Full Program
                </button>
              </div>
            )} */}
            {activeFilter === FILTERS.ALL && (
              <div style={{ margin: "10px 0 14px" }}>
                <a href="/IWPFI2026_ProgramDownload.pdf" download="IWPFI 2026 Full Program.pdf" className="reg-button"> 📥 Download Full Program</a>
              </div>
            )}
        {showWed && (
          <section id="wednesday" className="program-section">
            <p className="program-day-note">Wednesday, September 23, 2026</p>

            <div className="program-table-wrapper">
              <table className="program-table">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Time</th>
                    <th style={{ width: "25%" }}>Session / Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>2:00–5:00 pm</td>
                    <td>Registration</td>
                    <td>
                      <span className="room">Room: Hotel Lobby</span>
                      <br />
                      Registration and Conference Material Pick-up
                    </td>
                  </tr>
                  <tr>
                    <td>5:00–7:00 pm</td>
                    <td>Welcome Reception</td>
                    <td>
                      <span className="room">Room: Terrasse Après-Ski, Soutana I & II</span>
                      <br />
                      Join us for a warm welcome reception open to all registered
                      participants. Enjoy light refreshments and beverages as you
                      mingle and reconnect with colleagues and new friends.
                      <br />
                      <span className="program-note">
                        *Please ensure you selected the Welcome Reception option
                        during registration.
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Thursday */}
        {showThu && (
          <section id="thursday" className="program-section">
            <p className="program-day-note">Thursday, September 24, 2026</p>
            {/* <p className="program-mc">
              Master of Ceremonies: Dr. Mark Schiebler (AM), Dr. David Levin (PM)
            </p> */}

            <div className="program-table-wrapper">
              <table className="program-table">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Time</th>
                    <th style={{ width: "25%" }}>Session / Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>6:30–7:45 am</td>
                    <td>Registration</td>
                    <td>
                      <span className="room">Room: Foyer of Mali I</span>
                      <br />
                      Registration and Conference Material Pick-up
                    </td>
                  </tr>
                  <tr>
                    <td>6:30–7:45 am</td>
                    <td>Breakfast</td>
                    <td>
                      <span className="room">Room: Mali I & II</span>
                      <br />
                      For registered delegates only
                    </td>
                  </tr>
                  <tr>
                    <td>7:45–7:55 am</td>
                    <td>Welcome and Introductions</td>
                    <td><span className="room">Room: Mali III & IV</span>
                    <br />
                    Dr. Grace Parraga (Canada) and Dr. David Lewis Levin (USA)
                    </td>
                  </tr>
                  <tr>
                    <td>7:55–8:00 am</td>
                    <td>Introduction</td>
                    <td><span className="room">Room: Mali III & IV</span>
                    <br />
                    Dr. Mark Schiebler (USA)
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={3} className="program-session-header">
                      Session 1 – Asthma
                      <br />
                      <span className="program-session-mod">
                        Master of Ceremonies: Dr. Mark Schiebler (USA)<br />
                        Moderators:{" "}
                        <PersonLink personId="sean-fain" onOpen={openModalFor}>
                          Dr. Sean Fain (USA)
                        </PersonLink>{" "}
                        &amp;{" "}
                        <PersonLink
                          personId="sarah-svenningsen"
                          onOpen={openModalFor}
                        >
                          Dr. Sarah Svenningsen (Canada)
                        </PersonLink>
                      </span>
                    </td>
                  </tr>

                  <tr>
                    <td>8:00–8:15 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>(01) Integrating Advanced Airway Imaging with Clinical Practice to Improve Asthma Outcomes</strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="param-nair" onOpen={openModalFor}>
                        Dr. Param Nair (Canada)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-s1-0800"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Integrating Advanced Airway Imaging with Clinical Practice to Improve Asthma Outcomes"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>8:15–8:30 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>(02) Imaging to Guide Phenotyping and Treatment in Severe Asthma</strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="mario-castro" onOpen={openModalFor}>
                        Dr. Mario Castro (USA)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-s1-0815"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Imaging to Guide Phenotyping and Treatment in Severe Asthma"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>8:30–8:40 am</td>
                    <td>Q&amp;A</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>8:40–9:50 am</td>
                    <td>Paper presentations
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      8:40–8:50 am – (03) Hyperpolarized <sup>129</sup>Xe MRI Response Mapping Detects 
                      Regional Ventilation Redistribution in Response to Bronchodilator not seen with VDP
                      <br /> Presenter: Faiyza Alam (United Kingdom)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-0840"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Hyperpolarized 129Xe MRI Response Mapping Detects Regional Ventilation Redistribution in Response "
                          )
                        }
                      />
                      <br />
                      <br />
                      8:50–9:00 am – (04) Lobar Distribution and Properties of Airway Mucus-Plugs 
                      in Moderate-to-Severe Asthma: Insights from Chest CT 
                      <br /> Presenter: Eveline Durom (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-0850"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Lobar Distribution and Properties of Airway Mucus-Plugs in Moderate-to-Severe Asthma: Insights from Chest CT "
                          )
                        }
                      />
                      <br />
                      <br />
                      9:00–9:10 am – (05) 3D Phase-Resolved Functional Lung MRI as an Early Marker of Mepolizumab-Induced Treatment Changes in Severe Eosinophilic Asthma
                      <br />
                      Presenter: Filip Klimes (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-0940"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "3D Phase-Resolved Functional Lung MRI as an Early Marker of Mepolizumab-Induced Treatment Changes in Severe Eosinophilic Asthma"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:10–9:20 am – (06) <sup>129</sup>Xe MRI and Comprehensive Physiologic Testing in Patients with Asthma and/or COPD; Findings from the Advanced Diagnostic Profiling Study
                      <br />
                      Presenter: Helen Marshall (United Kingdom)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-0950"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "129Xe MRI and Comprehensive Physiologic Testing in Patients with Asthma and/or COPD"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:20–9:30 am – (07) CT-Evaluated Airway Tree Shape May be Clinically Relevant in Severe Asthma
                      <br />
                      Presenter: Nandhitha Ragunayakam (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1000"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "CT-Evaluated Airway Tree Shape May be Clinically Relevant in Severe Asthma"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:30–9:40 am – (08) Cyclical Airway and Gas Exchange Changes in Asthmatic Women Assessed with Multimodal Imaging
                      <br />
                      Presenter: Kimberly E. Schroeder (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1010"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Cyclical Airway and Gas Exchange Changes in Asthmatic Women Assessed with Multimodal Imaging"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:40–9:50 am – (09) <sup>129</sup>Xe MRI versus Clinical Measurements of Airway Obstruction in Severe Asthma: No Such Thing as "Normal"
                      <br />
                      Presenter: Alexander Matheson (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1020"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "129Xe MRI versus Clinical Measurements of Airway Obstruction in Severe Asthma"
                          )
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>9:50–10:30 am</td>
                    <td>Coffee break</td>
                    <td></td>
                  </tr>

                  <tr>
                    <td colSpan={3} className="program-session-header">
                      Session 2 – COPD
                      <br />
                      <span className="program-session-mod">
                        Master of Ceremonies: Dr. Mark Schiebler (USA)<br />
                        Moderators:{" "}
                        <PersonLink personId="miranda-kirby" onOpen={openModalFor}>
                          Dr. Miranda Kirby (Canada)
                        </PersonLink>{" "}
                        &amp;{" "}
                        <PersonLink
                          personId="benjamin-smith"
                          onOpen={openModalFor}
                        >
                          Dr. Benjamin Smith (Canada)
                        </PersonLink>
                      </span>
                    </td>
                  </tr>

                  <tr>
                    <td>10:30–10:45 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (10) Integration of Pulmonary Imaging into the Diagnosis of COPD
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="surya-bhatt" onOpen={openModalFor}>
                        Dr. Surya Bhatt (USA)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-s2-1030"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Integration of Pulmonary Imaging into the Diagnosis of COPD"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>10:45–11:00 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (11) Detecting and Treating Asthma and COPD Towards Better Outcomes
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="shawn-aaron" onOpen={openModalFor}>
                        Dr. Shawn Aaron (Canada)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-s2-1045"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Detecting and Treating Asthma and COPD Towards Better Outcomes"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>11:00–11:10 am</td>
                    <td>Q&amp;A</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>11:10 am–12:20 pm</td>
                    <td>Paper presentations
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      11:10–11:20 am – (12) Ultra-Low-Dose Dark-Field Radiography for the Diagnosis and Staging of Pulmonary Emphysema
                      <br />
                      Presenter: Henriette Bast (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1110"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Ultra-Low-Dose Dark-Field Radiography for the Diagnosis and Staging of Pulmonary Emphysema"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:20–11:30 am – (13) Computed Tomography Rib Density Is Associated with Longitudinal Outcomes in COPD
                      <br />
                      Presenter: Kalysta Makimoto (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1120"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Computed Tomography Rib Density Is Associated with Longitudinal Outcomes in COPD"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:30–11:40 am – (14) Quantitative Machine Learning Assessment of Pulmonary Texture Alterations in Dark-Field Radiography of Emphysema Lungs
                      <br />
                      Presenter: Sofia Demianova (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1130"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Quantitative Machine Learning Assessment of Pulmonary Texture Alterations in Dark-Field Radiography of Emphysema Lungs"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:40–11:50 am – (15) X-ray Velocimetry Provides Temporally and Spatially-Resolved Biomarkers of Lung Ventilation in Small Airways Disease
                      <br />
                      Presenter: Bradley W. Richmond (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1140"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "X-ray Velocimetry Provides Temporally and Spatially-Resolved Biomarkers of Lung Ventilation in Small Airways Disease"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:50 am–12:00 pm – (16) Multi-Disease Evaluation of Dark-field Chest Radiography: Detecting and Differentiating Emphysema and Pneumonia
                      <br />
                      Presenter: Lennard Kaster (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1150"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Multi-Disease Evaluation of Dark-field Chest Radiography: Detecting and Differentiating Emphysema and Pneumonia"
                          )
                        }
                      />
                      <br />
                      <br />
                      12:00–12:10 pm – (17) Oropharyngeal Enlargement-related Structural and Functional Impairment in Patients with Obstructive Lung Disease
                      <br />
                      Presenter: Jiwoong Choi (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1200"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Oropharyngeal Enlargement-related Structural and Functional Impairment in Patients with Obstructive Lung Disease"
                          )
                        }
                      />
                      <br />
                      <br />
                      12:10–12:20 pm – (18) AI-Based Chest CT Body Composition Analysis for Sarcopenia Risk Stratification in COPD: A Nation-Wide Multicenter Study
                      <br />
                      Presenter: Verena Wagner (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1210"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "AI-Based Chest CT Body Composition Analysis for Sarcopenia Risk Stratification in COPD: A Nation-Wide Multicenter Study"
                          )
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      12:20–1:25 pm
                      <br />
                      <br />
                      <br />
                      <br />
                      12:45–1:05 pm
                    </td>
                    <td>
                      Lunch
                      <br />
                      <br />
                      <span className="roomspace">Room: Mali I & II</span>
                      <br/>
                      Industry Innovation Spotlight
                    </td>
                    <td>
                      <span className="room">Room: Mali I & II</span>
                      <br />
                      For registered delegates only
                      <br />
                      <br />
                      3-min presentations from GSK, Coreline Soft, NVision and VIDA
                      <br />
                      Moderator: Jessica J. Sieren (USA)
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={3} className="program-session-header">
                      Session 3 – Lung Cancer
                      <br />
                      <span className="program-session-mod">
                        Master of Ceremonies: Dr. David Levin (USA) <br />
                        Moderators:{" "}
                        <PersonLink personId="laura-walkup" onOpen={openModalFor}>
                          Dr. Laura Walkup (USA)
                        </PersonLink>{" "}
                        &amp;{" "}
                        <PersonLink personId="ho-yun-lee" onOpen={openModalFor}>
                          Dr. Ho Yun Lee (South Korea)
                        </PersonLink>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>1:25–1:30 pm</td>
                    <td>Introduction</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>1:30–1:45 pm</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (19) Xenon MRI a Journey with a Noble Gas from the Workshop to the Ward
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="jim-wild" onOpen={openModalFor}>
                        Dr. Jim Wild (United Kingdom)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-s3-1330"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Xenon MRI a Journey with a Noble Gas from the Workshop to the Ward"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>1:45–2:00 pm</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (20) A View of The Physiology of Lung Cancer Resections with Technegas SPECT
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink
                        personId="greg-king-speaker"
                        onOpen={openModalFor}
                      >
                        Dr. Greg King (Australia)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-s3-1345"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "A View of The Physiology of Lung Cancer Resections with Technegas SPECT"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>2:00–2:10 pm</td>
                    <td>Q&amp;A</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>2:10–2:30 pm</td>
                    <td>Paper Presentations
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      2:10–2:20 pm – (21) Airway Count and Diameter Measurements on Photon-Counting CT: Comparison Between Lung Cancer Screening–Dose and Chest X-Ray–Equivalent Dose Studies
                      <br />
                      Presenter: Ryo Uemura (Japan)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1410"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Airway Count and Diameter Measurements on Photon-Counting CT: Comparison Between Lung Cancer Screening–Dose and Chest X-Ray–Equivalent Dose Studies"
                          )
                        }
                      />
                      <br />
                      <br />
                      2:20–2:30 pm – (22) Generating Per-Pixel Bone Attenuation-Contribution Maps in Chest Radiographs using Machine Learning
                      <br />
                      Presenter: Tina Dorosti (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1420"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Generating Per-Pixel Bone Attenuation-Contribution Maps in Chest Radiographs using Machine Learning"
                          )
                        }
                      />
                    </td>
                  </tr>
                  
                  <tr>
                    <td>2:30–2:55 pm</td>
                    <td>Coffee Break</td>
                    <td></td>
                  </tr>
                  
                  {/* LUNG CANCER PAPER PRESENTATIONS - NEXT 5 */}
                  <tr>
                    <td>2:55–3:45 pm</td>
                    <td>Paper Presentations
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      2:55–3:05 pm – (23) Healthy Ventilating Lung: A Jacobian-Informed Integrated CT Biomarker of Lung Structure and Mechanics in COPD
                      <br />
                      Presenter: Muhammad Faizyab Ali Chaudhary (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1455"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Healthy Ventilating Lung: A Jacobian-Informed Integrated CT Biomarker of Lung Structure and Mechanics in COPD"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:05–3:15 pm – (24) Ventilation Defect Mapping in <sup>129</sup>Xe MRI via Generative Anomaly Detection
                      <br />
                      Presenter: Akshdeep Sandhu (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1505"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Ventilation Defect Mapping in 129Xe MRI via Generative Anomaly Detection"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:15–3:25 pm – (25) Prognostic Impact of Ipsilateral Compensatory Lung Growth on Disease-Free Survival in Patients with Stage I Non-Small Cell Lung Cancer Following Lobectomy
                      <br />
                      Presenter: Mi Jung Park (South Korea)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1515"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Prognostic Impact of Ipsilateral Compensatory Lung Growth on Disease-Free Survival in Patients with Stage I Non-Small Cell Lung Cancer Following Lobectomy"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:25–3:35 pm – (26) 3D PREFUL MRI Reveals Increased Ventilation Heterogeneity in Pediatric Patients Following Lung Transplantation
                      <br />
                      Presenter: Milan Speth (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1525"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "3D PREFUL MRI Reveals Increased Ventilation Heterogeneity in Pediatric Patients Following Lung Transplantation"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:35–3:45 pm – (27) Functional <sup>129</sup>Xe MRI for Treatment Monitoring in Lung Cancer Patients Undergoing Radiation Therapy
                      <br />
                      Presenter: Riley Meyer (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="thu-1535"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Functional 129Xe MRI for Treatment Monitoring in Lung Cancer Patients Undergoing Radiation Therapy"
                          )
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>3:45–3:55 pm</td>
                    <td>Transition to poster session (cocktails will be served)</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>3:55–4:55 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session I
                      <br />
                       (10 min presentation per poster including discussion time)
                      <br />
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>1. Gatineau Station</strong><br />
                      Moderator: Dr. Yasutaka Nakano (Japan)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      3:55–4:05 pm &bull; (28) Radiomics-based Assessment of Lung Phantoms Using X-ray Dark-Field Computer Tomography (Sofia Demianova, Germany)<br /><br />
                      4:05–4:15 pm &bull; (29) Multi-Scale and Multi-Modal Characterization of Lung Tissue Specimens (Christian Dullin, Germany)<br /><br />
                      4:15–4:25 pm &bull; (30) Quantification Method Matters: <sup>129</sup>Xe MRI VDP as a Clinical Trial Endpoint (Yonni Friedlander, Canada)<br /><br />
                      4:25–4:35 pm &bull; (31) Analysis of Pulsatile Vessel Expansion in Healthy, COPD-and PH-Patients Using Dynamic Vessel Segmentation in Free-Breathing Lung MRI (Julian Glandorf, Germany)<br /><br />
                      4:35–4:45 pm &bull; (32) Machine Learning Integration of <sup>129</sup>Xe MRI, Quantitative CT, and Pulmonary Function Tests to Predict Short-Term Treatment Response in Severe Uncontrolled Asthma (Yixiu He, Canada)
                    </td>
                  </tr>
                  <tr>
                    <td>3:55–4:55 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session I
                      <br />
                       (10 min presentation per poster including discussion time)
                      <br />
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>2. Montreal Station</strong><br />
                      Moderator: Dr. Mark Wielpuetz (Germany)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      3:55–4:05 pm &bull; (33) Unsupervised Multi-Step Co-Registration of Diffusion-Weighted MRI and CT in NSCLC (Christian Marcel Heidt, Germany)<br /><br />
                      4:05–4:15 pm &bull; (34) Photon-Counting CT for Dynamic Lung Perfusion: Validation of a Low-Dose Protocol in a Porcine Lung Transplantation Model (Hoen-oh Shin, Germany)<br /><br />
                      4:15–4:25 pm &bull; (35) AI Pipeline for Pulmonary Embolism on Ventilation/Perfusion Imaging—VQ-SPRINT: Segmentation, Pseudo-planar Generation, and Registration INtegration Tool (Amir Jabbarpour, Canada)<br /><br />
                      4:25–4:35 pm &bull; (36) Pulmonary Ventilation MRI for COPD Phenotyping Focused on Airway Predominant (Seokwon Lee, South Korea)<br /><br />
                      4:35–4:45 pm &bull; (37) <sup>129</sup>Xe MRI Ventilation Defect Response to Inhaled and Biologic Therapies in Asthma: Relationship With Distal Airways Resistance (Sarah D Malik, Canada)<br /><br />
                      4:45–4:55 pm &bull; (38) Deep Learning-Based Motion Correction: Cardiac Motion Artifact and Image Quality Improvements on Chest CT (Masahiro Endo, Japan)
                    </td>
                  </tr>
                  <tr>
                    <td>3:55–4:55 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session I
                      <br />
                       (10 min presentation per poster including discussion time)
                      <br />
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>3. Saguenay Fjord Station</strong><br />
                      Moderator: Dr. Rachel Eddy (Canada)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      3:55–4:05 pm &bull; (39) A Pilot Study of Use of MRI to Measure Modulation of Eosinophil Related Inflammation in COPD (Helen Marshall, United Kingdom)<br /><br />
                      4:05–4:15 pm &bull; (40) Illuminating Pulmonary Function on CT with Integrated <sup>129</sup>Xe MRI Gas Exchange Maps (David Mummy, USA)<br /><br />
                      4:15–4:25 pm &bull; (41) Imaging Predictors of Physical Activity Improvement in COPD Patients Using a Self-Management Support Application (Emiko Ogawa, Japan)<br /><br />
                      4:25–4:35 pm &bull; (42) Imaging Markers of Augmented Disease Burden in Young Adults Diagnosed With Mild Asthma (Zahra Roozmand, Canada)<br /><br />
                      4:35–4:45 pm &bull; (43) Feasibility of Free Breathing, Contrast-Free 3D PREFUL MRI for Pulmonary Ventilation Assessment in Pediatric Non–Cystic Fibrosis Bronchiectasis (Milan Speth, Germany)
                    </td>
                  </tr>
                  <tr>
                    <td>3:55–4:55 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster I
                      <br />
                       (10 min presentation per poster including discussion time)
                      <br />
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>4. Gaspé Station</strong><br />
                      Moderator: Dr. Juergen Biederer (Germany)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      3:55–4:05 pm &bull; (44) Computed Tomography Features and Temporal Changes of Intrathoracic Herniation of Elastofibroma Dorsi After Thoracic Surgery (Nanae Tsuchiya, Japan)<br /><br />
                      4:05–4:15 pm &bull; (45) Complementary Role of CT Biomarkers to DLco in Predicting Severe Immune-Related Pneumonitis in NSCLC (Jonghoon Kim, South Korea)<br /><br />
                      4:15–4:25 pm &bull; (46) Image-Guided Bronchoscopy Samples Post-exacerbation Correlated with Longitudinal CT Airway Remodeling (Marrissa J. McIntosh, Canada)<br /><br />
                      4:25–4:35 pm &bull; (47) Impact of Field Strength and Inhomogeneities on Pulmonary R2* Estimates (Riley Meyer, USA)<br /><br />
                      4:35–4:45 pm &bull; (48) Multi-Phase Artifact Reduction/Correction (MARC) Deep Learning Model for 4DCT (Erik Cole, USA)<br /><br />
                      4:45–4:55 pm &bull; (49) Pulmonary Vascular Changes Following Endoscopic Lung Volume Reduction in COPD (Carley Stewart, Germany)
                    </td>
                  </tr>
                  {/* <tr>
                    <td>5:00–6:00 pm</td>
                    <td>Outdoor activities</td>
                    <td>Optional outdoor activities (own expenses)</td>
                  </tr> */}
                  <tr>
                    <td>5:00 pm</td>
                    <td>Free time</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Friday */}
        {showFri && (
          <section id="friday" className="program-section">
            <p className="program-day-note">Friday, September 25, 2026</p>
            {/* <p className="program-mc">
              Master of Ceremonies: Dr. Hans-Ulrich Kauczor (AM)
            </p> */}

            <div className="program-table-wrapper">
              <table className="program-table">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Time</th>
                    <th style={{ width: "25%" }}>Session / Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      6:30–7:40 am
                      <br />
                      <br />
                      <br />
                      7:00–7:15 am
                    </td>
                    <td>
                      Breakfast
                      <br />
                      <br />
                      <span className="roomspace">Room: Mali I & II</span>
                      <br/>
                      Industry Innovation Spotlight
                    </td>
                    <td>
                      <span className="room">Room: Mali I & II</span>
                      <br />
                      For registered delegates only
                      <br />
                      <br />
                      3-min presentations from AstraZeneca, Canon, and Cyclomedica
                      <br />
                      Moderator: Dr. Edwin J.R. van Beek (United Kingdom)
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={3} className="program-session-header">
                      Session 4 – Pulmonary Vascular Disease
                      <br />
                      <span className="program-session-mod">
                        Master of Ceremonies: Dr. Hans-Ulrich Kauczor (Germany) <br />
                        Moderators:{" "}
                        <PersonLink personId="helen-marshall" onOpen={openModalFor}>
                          Dr. Helen Marshall (United Kingdom)
                        </PersonLink>{" "}
                        &amp;{" "}
                        <PersonLink personId="k-yeol-lee" onOpen={openModalFor}>
                          Dr. Ki Yeol Lee (South Korea)
                        </PersonLink>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>7:40–7:45 pm</td>
                    <td>Introduction</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>7:45–8:00 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (50) Emerging <sup>129</sup>Xe MRI/MRS Markers for Pulmonary Vascular Disease
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink
                        personId="kn-bastiaan-driehuys"
                        onOpen={openModalFor}
                      >
                        Dr. Bastiaan Driehuys (USA)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-s4-0745"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            " Emerging 129Xe MRI/MRS Markers for Pulmonary Vascular Disease"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>8:00–8:15 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (51) Using Imaging to Improve Outcomes for Patients with Pulmonary Hypertension
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="david-kiely" onOpen={openModalFor}>
                        Dr. David Kiely (United Kingdom)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-s4-0800"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Using Imaging to Improve Outcomes for Patients with Pulmonary Hypertension"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>8:15–8:25 am</td>
                    <td>Q&amp;A</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>8:25–9:35 am</td>
                    <td>Paper presentations
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      8:25–8:35 am – (52) Artificial Intelligence-Enabled Detection of Vascular Perfusion Defects on Ventilation/Perfusion (V/Q) Scintigraphy for Pulmonary Embolism
                      <br />
                      Presenter: Amir Jabbarpour (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0825"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Artificial Intelligence-Enabled Detection of Vascular Perfusion Defects on Ventilation/Perfusion (V/Q) Scintigraphy for Pulmonary Embolism"
                          )
                        }
                      />
                      <br />
                      <br />
                      8:35–8:45 am – (53) Probing Regional Gas Exchange Dynamics with Hyperpolarized Xenon MRI Exchange Time Imaging
                      <br />
                      Presenter: Peter Niedbalski (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0835"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Probing Regional Gas Exchange Dynamics with Hyperpolarized Xenon MRI Exchange Time Imaging"
                          )
                        }
                      />
                      <br />
                      <br />
                      8:45–8:55 am – (54) On the Construction of a Large-Scale Database of Annotated Planar and SPECT Lung Ventilation-Perfusion Scintigraphy for Pulmonary Embolism (VQ4PEDB)
                      <br />
                      Presenter: Ran Klein (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0845"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "On the Construction of a Large-Scale Database of Annotated Planar and SPECT Lung Ventilation-Perfusion Scintigraphy for Pulmonary Embolism (VQ4PEDB)"
                          )
                        }
                      />
                      <br />
                      <br />
                      8:55–9:05 am – (55) Synchrotron Phase-Contrast CT of Human Lungs at Clinical Dose Levels: From Fixed to Unfixed Explants Toward Clinical Translation
                      <br />
                      Presenter: Claudia Victoria Benke (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0855"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Synchrotron Phase-Contrast CT of Human Lungs at Clinical Dose Levels: From Fixed to Unfixed Explants Toward Clinical Translation"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:05–9:15 am – (56) Arterial vs Venous CT Vessel Counts in COPD: Stage-Specific Drivers of Functional Impairment and Disease Progression
                      <br />
                      Presenter: Gaurav Veer Singh (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0905"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Arterial vs Venous CT Vessel Counts in COPD: Stage-Specific Drivers of Functional Impairment and Disease Progression"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:15–9:25 am – (57) The CaliFloret Xenon MRI Sequence: a Novel Method for Single-Breath Spectroscopy and Imaging
                      <br />
                      Presenter: Zachary T Money-Joslyn (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0915"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "The CaliFloret Xenon MRI Sequence: a Novel Method for Single-Breath Spectroscopy and Imaging"
                          )
                        }
                      />
                      <br />
                      <br />
                      9:25–9:35 am – (58) Short-Term Longitudinal Variability of CT-Derived Pulmonary Vascular Volume Measurements in Severe Asthma
                      <br />
                      Presenter: Anusha A. Mappanasingam (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-0925"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Short-Term Longitudinal Variability of CT-Derived Pulmonary Vascular Volume Measurements in Severe Asthma"
                          )
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>9:35–10:15 am</td>
                    <td>Coffee break</td>
                    <td></td>
                  </tr>

                  <tr>
                    <td colSpan={3} className="program-session-header">
                      Session 5 – ILD and Fibrosis
                      <br />
                      <span className="program-session-mod">
                        Master of Ceremonies: Dr. Hans-Ulrich Kauczor (Germany)<br />
                        Moderators:{" "}
                        <PersonLink personId="joon-beom-seo" onOpen={openModalFor}>
                          Dr. Joon Beom Seo (South Korea)
                        </PersonLink>{" "}
                        &amp;{" "}
                        <PersonLink
                          personId="yasutaka-nakano" onOpen={openModalFor}>
                          Dr. Yasu Nakano (Japan)
                        </PersonLink>
                      </span>
                    </td>
                  </tr>

                  <tr>
                    <td>10:15–10:30 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (59) A Microscopic View of the Lung: Endobronchial Optical Coherence Tomography 
                        for Early Detection, Diagnosis and Disease Monitoring</strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="lida-hariri" onOpen={openModalFor}>
                        Dr. Lida Hariri (USA)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-s5-1015"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            `A Microscopic View of the Lung: Endobronchial Optical Coherence Tomography 
                            for Early Detection, Diagnosis and Disease Monitoring`
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>10:30–10:45 am</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong> (60) The Role of Imaging to Understand Mechanisms of Fibrosis</strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="joaee-choe" onOpen={openModalFor}>
                        Dr. Jooae Choe (South Korea)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-s5-1030"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "The Role of Imaging to Understand Mechanisms of Fibrosis"
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>10:45–10:55 am</td>
                    <td>Q&amp;A</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>10:55 am–12:05 pm</td>
                    <td>Paper presentations
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      10:55–11:05 am – (61) Lung Cancer Risk in Idiopathic Pulmonary Fibrosis: Independent Effects of Baseline Fibrosis and Emphysema and the Impact of Longitudinal Fibrosis Trajectories
                      <br />
                      Presenter: Woocheol Kwon (South Korea)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1055"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Lung Cancer Risk in Idiopathic Pulmonary Fibrosis: Independent Effects of Baseline Fibrosis and Emphysema and the Impact of Longitudinal Fibrosis Trajectories"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:05–11:15 am – (62) Deep Learning Micro-CT: A Novel Platform for Quantitative Assessment of Airway and Vascular Remodelling in Pulmonary Fibrosis
                      <br />
                      Presenter: Rachel Brady (United Kingdom)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1105"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Deep Learning Micro-CT: A Novel Platform for Quantitative Assessment of Airway and Vascular Remodelling in Pulmonary Fibrosis"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:15–11:25 am – (63) Comparison of Phase-Resolved Functional Lung (PREFUL) MRI and CT Parametric Response Mapping (PRM) in COSYCONET COPD
                      <br />
                      Presenter: Andreas Voskrebenzev (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1115"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Comparison of Phase-Resolved Functional Lung (PREFUL) MRI and CT Parametric Response Mapping (PRM) in COSYCONET COPD"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:25–11:35 am – (64) Texture Analysis of <sup>129</sup>Xe MRI Ventilation and Respiratory Symptoms in Young People who Smoke or Vape
                      <br />
                      Presenter: Roy Li (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1125"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Texture Analysis of 129Xe MRI Ventilation and Respiratory Symptoms in Young People who Smoke or Vape"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:35–11:45 am – (65) Long-Term Follow-Up of AI-Derived Quantitative HRCT Metrics in Patients with UIP According to the Presence or Absence of Antifibrotic Therapy: A Retrospective Cohort Study
                      <br />
                      Presenter: Gong Yong Jin (South Korea)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1135"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Long-Term Follow-Up of AI-Derived Quantitative HRCT Metrics in Patients with UIP According to the Presence or Absence of Antifibrotic Therapy: A Retrospective Cohort Study"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:45–11:55 am – (66) Characterization of Lung Structure using Free-Breathing Zero-Time-to-Echo MRI
                      <br />
                      Presenter: Sean B. Fain (USA)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1145"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Characterization of Lung Structure using Free-Breathing Zero-Time-to-Echo MRI"
                          )
                        }
                      />
                      <br />
                      <br />
                      11:55 am–12:05 pm – (67) Non-Contrast CT Ventilation Imaging Discriminates Obstructive Lung Disease From Health: A Comparison of Intensity, Jacobian, and Hybrid Ventilation Methods
                      <br />
                      Presenter: William Clark (United Kingdom)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1155"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Non-Contrast CT Ventilation Imaging Discriminates Obstructive Lung Disease From Health: A Comparison of Intensity, Jacobian, and Hybrid Ventilation Methods"
                          )
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      12:05–1:10 pm
                      <br />
                      <br />
                      <br />
                      <br />
                      12:20–12:40 pm
                    </td>
                    <td>
                      Lunch
                      <br />
                      <br />
                      <span className="roomspace">Room: Mali I & II</span>
                      <br/>
                      Industry Innovation Forum
                    </td>
                    <td>
                      <span className="room">Room: Mali I & II</span>
                      <br />
                      For registered delegates only
                      <br />
                      <br />
                      20-min conversation – GSK
                      <br />
                      <strong>Airways Disease, Biologics and Imaging – Past, Present and Future</strong>
                      <br />
                      Speakers: Dr. Rachel Eddy (Canada) and Sarah Svenningsen (Canada)  
                      <br />
                      Moderator: Dr. Grace Parraga (Canada)
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="program-session-header">
                    <span className="program-session-mod">
                        Master of Ceremonies: Dr. Edwin J.R. van Beek (United Kingdom)
                  </span>
                  </td>
                  </tr>
                  <tr>
                    <td>1:10–1:15 pm</td>
                    <td>Introduction</td>
                    <td></td>
                  </tr>
                  <tr></tr>
                  <tr>
                    <td>1:15–2:20 pm</td>
                    <td>John West Medalists</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      1:15–1:25 pm – Introduction by <PersonLink 
                      personId="joon-beom-seo" onOpen={openModalFor}>
                          Dr. Joon Beom Seo (South Korea)
                        </PersonLink> 
                      {/* <PersonLink
                          personId="edwin-van-beek"
                          onOpen={openModalFor}
                        >
                          Prof. Edwin J.R. van Beek (United Kingdom)
                        </PersonLink> and  */}
                        
                      <br />
                      <br />
                      1:25–1:45 pm – (68) What Do We Know about Metabolic Imaging ?
                      <br />
                      Speaker: <PersonLink
                          personId="schiebler-mark"
                          onOpen={openModalFor}
                        >
                          Dr. Mark L. Schiebler (USA) 
                        </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1345"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "What Do We Know about Metabolic Imaging?"
                          )
                        }
                      />
                      <br />
                      <br />
                      1:45–2:05 pm – (69) A Journey with Curiosity and Serendipity: Anatomic and Functional Pulmonary Imaging, Interstitial Lung Abnormalities, and Drug-related Lung Disease.
                      <br />
                      Speaker: <PersonLink 
                      personId="hiroto-hatabu" 
                      onOpen={openModalFor}
                      >
                        Dr. Hiroto Hatabu (Japan)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1325"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "A Journey with Curiosity: Anatomic and functional pulmonary imaging, Interstitial Lung Abnormalities, and Drug-related Lung Disease."
                          )
                        }
                      />
                      
                      <br />
                      <br />
                      2:05–2:20 pm – Discussion
                    </td>
                  </tr>

                  <tr>
                    <td>2:20–2:50 pm</td>
                    <td>Coffee break</td>
                    <td></td>
                  </tr>

                  <tr>
                    <td>2:50–3:10 pm</td>
                    <td>Keynote speaker</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      <strong>
                        (70) Air Pollution, Myocardial Fibrosis and Coronary Atherosclerosis: 
                        Embedding Sustainability into Imaging and Patient Care
                      </strong>
                      <br />
                      Speaker:{" "}
                      <PersonLink personId="kate-hanneman" onOpen={openModalFor}>
                        Dr. Kate Hanneman (Canada)
                      </PersonLink>
                      <br />
                      Moderator:{" "}
                      <PersonLink personId="talissa-altes" onOpen={openModalFor}>
                        Dr. Talissa Altes (USA)
                      </PersonLink>
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1450"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            `Air Pollution, Myocardial Fibrosis and Coronary Atherosclerosis 
                            : Embedding Sustainability into Imaging and Patient Care`
                          )
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>3:10–4:05 pm</td>
                    <td>Young Investigator Forum
                      <br />
                      (7 min presentation + 3 min Q&A) 
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      3:10–3:15 pm – Introduction by{" "}
                      <PersonLink personId="hiroto-hatabu" onOpen={openModalFor}>
                        Dr. Hiroto Hatabu (Japan)
                      </PersonLink> 
                      <br />
                      <br />
                      Moderator: {" "}
                      <PersonLink personId="sang-lee" onOpen={openModalFor}>
                        Dr. Sang Min Lee (South Korea)
                      </PersonLink> 
                      <br />
                      <br />
                      3:15–3:25 pm – (71) Photon-Counting CT-based Lung Ventilation and Perfusion: Reference Ranges and Regional Variability in 103 Healthy Adults
                      <br />
                      Presenter: Julian Dittrich (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1515"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Photon-Counting CT-based Lung Ventilation and Perfusion: Reference Ranges and Regional Variability in 103 Healthy Adults"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:25–3:35 pm – (72) Quantitative Evaluation of CT Mucus-Plug Characteristics in Asthma and COPD
                      <br />
                      Presenter: Sam Tcherner (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1525"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Quantitative Evaluation of CT Mucus-Plug Characteristics in Asthma and COPD"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:35–3:45 pm – (73) A Multimodal Model Incorporating Quantitative Imaging Improves Prognostic Discrimination Beyond Pathologic Staging in NSCLC
                      <br />
                      Presenter: You Jin Oh (South Korea)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1535"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "A Multimodal Model Incorporating Quantitative Imaging Improves Prognostic Discrimination Beyond Pathologic Staging in NSCLC"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:45–3:55 pm – (74) Deep-learning Based Pixel-wise Lung Thickness Maps for Volume Estimation in Chest Radiographs
                      <br />
                      Presenter: Tina Dorosti (Germany)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Deep-learning Based Pixel-wise Lung Thickness Maps for Volume Estimation in Chest Radiographs"
                          )
                        }
                      />
                      <br />
                      <br />
                      3:55–4:05 pm – (75) Impact of <sup>129</sup>Xe MRI VDP and Quantification Methods and Ventilation Heterogeneity on Prediction of Lung Resection Outcomes
                      <br />
                      Presenter: Om Thakar (Canada)
                      <br />
                      <LearningObjectivesLink
                        sessionId="fri-1555"
                        onOpen={(sid) =>
                          openObjectivesFor(
                            sid,
                            "Impact of 129Xe MRI VDP and Quantification Methods and Ventilation Heterogeneity on Prediction of Lung Resection Outcomes"
                          )
                        }
                      />
                      <br />
                      
                    </td>
                  </tr>

                  {/* <tr>
                    <td>3:15–4:05 pm</td>
                    <td>Young Investigator Forum</td>
                    <td>Oral presentations</td>
                  </tr> */}
                  <tr>
                    <td>4:05–4:15 pm</td>
                    <td>Transition to poster session (cocktails will be served)</td>
                    <td></td>
                  </tr>
                  <tr></tr>
                  <tr>
                    <td>4:15–5:15 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session II
                      <br />
                      (10 min presentation per poster including discussion time)
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>5. Quebec City Station</strong><br />
                      Moderator: Dr. Jens Vogel-Claussen (Germany) <br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      4:15–4:25 pm &bull; (76) Comparison of TE-Dependent Observed Lung T1 Measured Using 3d and 2d Inversion Recovery Look-Locker (Simon M.F. Triphan, Germany)<br /><br />
                      4:25–4:35 pm &bull; (77) Dynamic Digital Radiography for Functional Assessment of Diaphragmatic Dysfunction: A Pictorial Review (Nanae Tsuchiya, Japan)<br /><br />
                      4:35–4:45 pm &bull; (78) Assessing Local Lung Function and Structure Using Xenon-Enhanced Spectral CT Imaging in Healthy and Pathological in Vivo Rabbit Lungs (Emma Verelst, Belgium)<br /><br />
                      4:45–4:55 pm &bull; (79) Mild Asthma may not be Mild: CT but not <sup>129</sup>Xe MRI Evidence of Pulmonary Vascular Remodeling (Reza Babaloo, Canada)<br /><br />
                      4:55–5:05 pm &bull; (80) Impact of Inflation Level on Breath-Hold 1H Magnetic Resonance Imaging Surrogates of Regional Lung Ventilation (William Clark, United Kingdom)
                    </td>
                  </tr>
                  <tr>
                    <td>4:15–5:15 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session II
                      <br />
                      (10 min presentation per poster including discussion time)
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>6. Nunavik Station</strong><br />
                      Moderator: Dr. Marrissa McIntosh (Canada)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      4:15–4:25 pm &bull; (81) CT-Evaluated Mucus Plugging in Sputum Cellular Phenotypes of Severe Asthma (Ekamdeep Sandhu, Canada)<br /><br />
                      4:25–4:35 pm &bull; (82) Initial Experience Translating Xe MRI from Research to Clinical Practice (Laura Walkup, USA)<br /><br />
                      4:35–4:45 pm &bull; (83) Graph Neural Network Modeling of Airway Structure for COPD Severity Classification and Functional Prediction (Erik J Cole, USA)<br /><br />
                      4:45–4:55 pm &bull; (84) Impact of Ventilation Defect Analysis Method on Test-Retest Repeatability of Hyperpolarized <sup>129</sup>Xe MRI in Young People with Cystic Fibrosis (Akshdeep Sandhu, Canada)<br /><br />
                      4:55–5:05 pm &bull; (85) Leveraging MR Elastography: Evaluating Technical Feasibility and Diagnostic Potential in Mediastinal Lesions (Sung Goo Park, South Korea)
                    </td>
                  </tr>
                  <tr>
                    <td>4:15–5:15 pm</td>
                    <td>CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session II
                      <br />
                      (10 min presentation per poster including discussion time)
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>7. Charlevoix Station</strong><br />
                      Moderator: Dr. Yoshiharu Ohno (Japan)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      (86) CANCELLED<br /><br />
                      4:15–4:25 pm &bull; (87) Investigation on Interstitial Lung Disease Using QW maps obtained from 3D UTE-MRI (Hyeonha Kim, South Korea)<br /><br />
                      4:25–4:35 pm &bull; (88) Quantitative CT Imaging and Oscillometry are Complementary Predictors of Baseline Lung Allograft Dysfunction (Daniel Genkin, Canada)<br /><br />
                      4:35–4:45 pm &bull; (89) Anatomy-Aware Active Learning for Pectoralis Muscle Segmentation on CT (Mustansir Verdawala, Canada)<br /><br />
                      4:45–4:55 pm &bull; (90) Associations between Oscillometry and Pulmonary Functional Metrics from Hyperpolarized Xenon MRI (Sean B. Fain, USA)
                    </td>
                  </tr>
                  <tr>
                    <td>4:15–5:15 pm</td>
                    <td>
                      CONCURRENT SESSION
                      <br />
                      <br />
                      <br />
                      Rapid Fire Digital Poster Session II
                      <br />
                      (10 min presentation per poster including discussion time)
                    </td>
                    <td>
                      <span className="room">Room: Mali III & IV</span><br />
                      <strong>8. Mont-Tremblant Station</strong><br />
                      Moderator: Dr. Yoshiyuki Ozawa (Japan)<br />
                      <LearningObjectivesLink
                        sessionId="thu-1545"
                        onOpen={(sid) =>
                          openObjectivesFor(sid, "Rapid Fire Digital Poster Session")
                        }
                      /><br /><br />
                      4:15–4:25 pm &bull; (91) CT Measures of Peripheral Pulmonary Arteries and Veins as Indicators of Heart Failure (Samuel Peterson, USA)<br /><br />
                      4:25–4:35 pm &bull; (92) Dynamic 19F Gas MRI in Phantom and Human Lungs using 3D Rosette UTE (PETALUTE) (Truc Nguyen, USA)<br /><br />
                      4:35–4:45 pm &bull; (93) Obstructive Lung Disease Classification Using Hyperpolarized Xenon Ventilation MRI: A Multi-Site AI and Reader Study (Redha Ali, USA)<br /><br />
                      4:45–4:55 pm &bull; (94) CT Airway Segmentation: Impact of Different Segmentation Methods on Quantitative Biomarkers in COPD (Allona Kaye Lim, Canada)<br /><br />
                      4:55–5:05 pm &bull; (95) Deep Learning Image Synthesis of Hyperpolarized <sup>129</sup>Xe MRI Ventilation Maps from Chest CT (Lixin Chu, Canada)<br /><br />
                      5:05–5:15 pm &bull; (96) Clinical Feasibility of Diagnostic-CT-Trained Lung Lobar Perfusion Quantification AI on Low-Dose CTAC SPECT/CT (Elaheh Amini, Canada)
                    </td>
                  </tr>
                  {/* <tr>
                    <td>6:00 pm</td>
                    <td>Gala and Lecture/YI Awards</td>
                    <td></td>
                  </tr> */}
                  <tr>
                    <td>6:00–6:45 pm</td>
                    <td>Cocktail</td>
                    <td><span className="room">Room: Foyer of Mali I & II</span>
                    <br />
                    Dressing Code: Business Formal / Cocktail Dress</td>
                  </tr>
                  <tr>
                    <td>6:45–7:00 pm</td>
                    <td>Gala – Introduction and Awards</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      Master of Ceremonies: Dr. Sarah Svenningsen (Canada) and Dr. Mark Schiebler (USA)
                    </td>
                  </tr>
                  <tr>
                    <td>7:00–7:45 pm</td>
                    <td>Guest Speaker and Book Signing</td>
                    <td>
                      <span className="room">Room: Mali III & IV</span>
                      <br />
                      The Road to Reconciliation: Lessons from Valley of the Birdtail<br />
                      Speaker: <PersonLink personId="sniderman-andrew" onOpen={openModalFor}>
                        Andrew Stobo Sniderman
                      </PersonLink>
                    </td>
                  </tr>
                  <tr>
                    <td>7:45–10:45 pm</td>
                    <td>Dinner and Dance with Traditional Band</td>
                    <td><span className="room">Room: Mali III & IV</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Saturday (always show only in full program, unless you want it filterable too) */}
        
        {showSat && (
          <section id="friday" className="program-section">
            <p className="program-day-note">Saturday, September 26, 2026</p>
            <div className="program-table-wrapper">
              <table className="program-table">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Time</th>
                    <th style={{ width: "25%" }}>Session / Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {/* <tr>
                    <td>6:00–9:00 am</td>
                    <td>Breakfast</td>
                    <td><span className="room">Room: Mali III & IV</span></td>
                  </tr> */}
                  <tr>
                    <td>8:00–9:00 am</td>
                    <td>Board Meeting (breakfast buffet available)</td>
                    <td><span className="room">Room: Kamichat</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}
        <p className="program-intro">
            This program was supported by an educational grant from Sanofi and Regeneron.
        </p>
        <div className="footer-logo">
          <img src="/images/iwpfi_horizontal_logo.jpg" alt="Logo" />
        </div>
      </main>

      <Footer />

      <PersonModal person={openPerson} onClose={() => setOpenPersonId(null)} />
      {/* NEW learning objectives modal */}
      <LearningObjectivesModal
        objectives={openObjectives}
        sessionTitle={openObjectivesSessionTitle}
        onClose={() => {
          setOpenObjectivesSessionId(null);
          setOpenObjectivesSessionTitle("");
        }}
      />
    </div>
  );
};

export default Program;